/**
 * ClientGanttChart - Gantt chart simplificat pentru portalul client
 * Afișează etapele proiectului pe timeline cu posibilitate de download PDF
 */
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Calendar, ChevronLeft, ChevronRight, TrendingUp, Clock } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

const PHASE_COLORS = [
  "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

const parseDate = (d) => d ? new Date(d) : null;
const getDaysBetween = (s, e) => { if (!s || !e) return 0; return Math.ceil(Math.abs(e - s) / 86400000); };

const formatCurrency = (v) => new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(v || 0);

export default function ClientGanttChart({ site, phases }) {
  const scrollRef = useRef(null);
  const [extraBefore, setExtraBefore] = useState(0);
  const [extraAfter, setExtraAfter] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const dayWidth = 28;

  const phasesWithDates = phases.filter(p => p.start_date && p.end_date);

  // Calculate timeline range from phases or site dates
  const getTimelineRange = () => {
    let allDates = [];
    if (phasesWithDates.length > 0) {
      allDates = phasesWithDates.flatMap(p => [new Date(p.start_date), new Date(p.end_date)]);
    } else if (site?.start_date && site?.end_date) {
      allDates = [new Date(site.start_date), new Date(site.end_date)];
    } else {
      const now = new Date();
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 2, 0) };
    }
    const min = new Date(Math.min(...allDates));
    const max = new Date(Math.max(...allDates));
    const start = new Date(min.getFullYear(), min.getMonth() - extraBefore, 1);
    const end = new Date(max.getFullYear(), max.getMonth() + 1 + extraAfter, 0);
    return { start, end };
  };

  const { start: timelineStart, end: timelineEnd } = getTimelineRange();
  const totalDays = getDaysBetween(timelineStart, timelineEnd) + 1;

  const getMonths = () => {
    const months = [];
    let current = new Date(timelineStart);
    while (current <= timelineEnd) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      months.push({ date: monthStart, endDate: monthEnd, label: current.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' }) });
      current.setMonth(current.getMonth() + 1);
    }
    return months;
  };

  const getDays = () => {
    const days = [];
    let current = new Date(timelineStart);
    while (current <= timelineEnd) {
      days.push({ date: new Date(current), dayNum: current.getDate(), isWeekend: current.getDay() === 0 || current.getDay() === 6, isToday: current.toDateString() === new Date().toDateString() });
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const months = getMonths();
  const days = getDays();

  const exportPDF = () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const m = { top: 45, bottom: 25, left: 15, right: 15 };
      const nameW = 55;
      const chartX = m.left + nameW;
      const chartW = pw - chartX - m.right;
      const td = totalDays;
      const dw = chartW / td;
      const rh = 16;

      // Header
      doc.setFillColor(13, 148, 136);
      doc.rect(0, 0, pw, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text(`Grafic Gantt - ${site?.name || 'Proiect'}`, m.left, 15);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`Generat: ${new Date().toLocaleDateString('ro-RO')}`, m.left, 23);
      if (site?.address) doc.text(site.address, m.left, 29);

      // Legend
      doc.setFillColor(34, 197, 94); doc.roundedRect(pw - 80, 12, 8, 4, 1, 1, 'F');
      doc.setFontSize(8); doc.text('Progres', pw - 70, 15);
      doc.setFillColor(37, 99, 235); doc.roundedRect(pw - 80, 18, 8, 4, 1, 1, 'F');
      doc.text('Timp', pw - 70, 21);

      let y = m.top;

      // Month headers
      doc.setFillColor(241, 245, 249);
      doc.rect(chartX, y, chartW, 8, 'F');
      let cd = new Date(timelineStart);
      while (cd <= timelineEnd) {
        const ms = new Date(cd);
        const dfs = getDaysBetween(timelineStart, ms);
        const x = chartX + dfs * dw;
        doc.setDrawColor(180, 180, 180); doc.line(x, y, x, y + 8);
        doc.setFontSize(7); doc.setTextColor(30, 41, 59);
        const ml = cd.toLocaleDateString('ro-RO', { month: 'short', year: '2-digit' });
        doc.text(ml, x + 2, y + 5);
        cd.setMonth(cd.getMonth() + 1);
      }

      y += 10;

      // Project name col header
      doc.setFillColor(240, 240, 240); doc.rect(m.left, m.top, nameW, 8, 'F');
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
      doc.text('ETAPA', m.left + 2, m.top + 5);

      // Phases
      phasesWithDates.forEach((phase, idx) => {
        if (y > ph - m.bottom - 20) { doc.addPage(); y = 20; }
        const ps = new Date(phase.start_date);
        const pe = new Date(phase.end_date);
        const dfs = getDaysBetween(timelineStart, ps);
        const dur = getDaysBetween(ps, pe) + 1;
        const bx = chartX + dfs * dw;
        const bw = Math.max(dur * dw, 20);
        const progress = phase.progress_percent || 0;
        const now = new Date();
        const timeProg = ps > now ? 0 : pe < now ? 100 : Math.round(getDaysBetween(ps, now) / getDaysBetween(ps, pe) * 100);

        if (idx % 2 === 0) { doc.setFillColor(250, 250, 250); doc.rect(m.left, y, pw - m.left - m.right, rh, 'F'); }

        // Phase name
        doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
        const pn = phase.name.length > 22 ? phase.name.substring(0, 22) + '..' : phase.name;
        doc.text(pn, m.left + 2, y + 6);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(100, 100, 100);
        doc.text(`${ps.toLocaleDateString('ro-RO')} - ${pe.toLocaleDateString('ro-RO')}`, m.left + 2, y + 11);

        // Bar background
        const color = phase.color || PHASE_COLORS[idx % PHASE_COLORS.length];
        const r = parseInt(color.slice(1, 3), 16), g = parseInt(color.slice(3, 5), 16), b = parseInt(color.slice(5, 7), 16);
        doc.setFillColor(220, 220, 220); doc.roundedRect(bx, y + 2, bw, rh - 4, 1, 1, 'F');

        // Progress fill
        if (progress > 0) {
          doc.setFillColor(r, g, b);
          doc.roundedRect(bx, y + 2, Math.min(bw * progress / 100, bw), (rh - 4) / 2, 1, 1, 'F');
        }
        if (timeProg > 0) {
          doc.setFillColor(37, 99, 235);
          doc.roundedRect(bx, y + 2 + (rh - 4) / 2, Math.min(bw * timeProg / 100, bw), (rh - 4) / 2, 1, 1, 'F');
        }

        if (bw > 25) {
          doc.setFontSize(6); doc.setTextColor(50, 50, 50);
          doc.text(`${progress}%`, bx + bw + 2, y + 7);
        }

        y += rh;
      });

      // Summary
      const sy = Math.min(y + 10, ph - 30);
      doc.setFillColor(241, 245, 249); doc.roundedRect(m.left, sy, pw - m.left - m.right, 15, 2, 2, 'F');
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 41, 59);
      doc.text(`Etape: ${phasesWithDates.length}`, m.left + 5, sy + 9);
      const avgProg = phasesWithDates.length > 0 ? Math.round(phasesWithDates.reduce((s, p) => s + (p.progress_percent || 0), 0) / phasesWithDates.length) : 0;
      doc.setFont('helvetica', 'normal');
      doc.text(`Progres mediu: ${avgProg}%`, m.left + 50, sy + 9);
      if (site?.total_value) doc.text(`Valoare: ${formatCurrency(site.total_value)}`, m.left + 110, sy + 9);

      doc.setFontSize(7); doc.setTextColor(150, 150, 150);
      doc.text('e-Tesa - Portal Client', pw / 2, ph - 8, { align: 'center' });

      const fileName = `Gantt_${(site?.name || 'Proiect').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);
      toast.success(`PDF exportat: ${fileName}`);
    } catch (err) {
      console.error(err);
      toast.error('Eroare la exportul PDF');
    } finally {
      setIsExporting(false);
    }
  };

  if (phasesWithDates.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-slate-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>Nu sunt etape cu date definite pentru graficul Gantt.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500" /><span>Progres fizic</span></div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500" /><span>Progres timp</span></div>
        </div>
        <Button onClick={exportPDF} disabled={isExporting} className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white" data-testid="client-gantt-export-pdf">
          {isExporting ? <><div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />Se exportă...</> : <><Download className="w-4 h-4 mr-2" />Export PDF</>}
        </Button>
      </div>

      <Card className="border-0 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
            <Button variant="ghost" size="sm" onClick={() => setExtraBefore(p => p + 1)}><ChevronLeft className="w-4 h-4 mr-1" />Luna ant.</Button>
            <span className="text-sm font-medium text-slate-600">{site?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => setExtraAfter(p => p + 1)}>Luna urm.<ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>

          <div className="overflow-x-auto" ref={scrollRef}>
            {/* Month headers */}
            <div className="sticky top-0 bg-white z-10 border-b border-slate-200">
              <div className="flex" style={{ width: `${totalDays * dayWidth}px` }}>
                {months.map((month) => {
                  const dim = getDaysBetween(month.date, month.endDate) + 1;
                  return (
                    <div key={month.label} className="border-r border-slate-200 bg-slate-50 px-2 py-1 text-sm font-medium" style={{ width: `${dim * dayWidth}px` }}>
                      {month.label}
                    </div>
                  );
                })}
              </div>
              <div className="flex" style={{ width: `${totalDays * dayWidth}px` }}>
                {days.map((day, idx) => (
                  <div key={`day-${day.date || idx}`}
                    className={`text-center text-xs border-r border-slate-100 py-1 ${day.isToday ? 'bg-blue-500 text-white font-bold' : day.isWeekend ? 'bg-slate-100 text-slate-400' : 'text-slate-600'}`}
                    style={{ width: `${dayWidth}px` }}>
                    {day.dayNum}
                  </div>
                ))}
              </div>
            </div>

            {/* Phase bars */}
            <div className="relative" style={{ width: `${totalDays * dayWidth}px` }}>
              {phasesWithDates.map((phase, idx) => {
                const ps = new Date(phase.start_date);
                const pe = new Date(phase.end_date);
                const offsetDays = getDaysBetween(timelineStart, ps);
                const durationDays = getDaysBetween(ps, pe) || 1;
                const left = offsetDays * dayWidth;
                const width = Math.max(durationDays * dayWidth, 60);
                const progress = phase.progress_percent || 0;
                const now = new Date();
                const timeProg = ps > now ? 0 : pe < now ? 100 : Math.round(getDaysBetween(ps, now) / getDaysBetween(ps, pe) * 100);
                const color = phase.color || PHASE_COLORS[idx % PHASE_COLORS.length];

                return (
                  <div key={phase.id} className="relative h-20 border-b border-slate-100 hover:bg-slate-50 group">
                    {/* Today line */}
                    {days.findIndex(d => d.isToday) >= 0 && (
                      <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-5" style={{ left: `${days.findIndex(d => d.isToday) * dayWidth + dayWidth / 2}px` }} />
                    )}
                    <div className="absolute top-2 h-16 rounded-lg shadow-md overflow-hidden" style={{ left: `${left}px`, width: `${width}px` }}>
                      <div className="absolute inset-0" style={{ backgroundColor: color, opacity: 0.2 }} />
                      {/* Progress */}
                      <div className="absolute top-0 left-0 h-8 transition-all" style={{ width: `${progress}%`, backgroundColor: color, opacity: 0.8 }} />
                      {/* Time progress */}
                      <div className="absolute bottom-0 left-0 h-8 bg-blue-500 opacity-60" style={{ width: `${timeProg}%` }} />
                      {/* Label */}
                      <div className="absolute inset-0 flex items-center justify-center px-2">
                        <span className="text-xs font-semibold text-slate-800 truncate drop-shadow-sm">{phase.name}</span>
                      </div>
                      <div className="absolute top-1 right-2 text-[10px] font-bold text-slate-700 drop-shadow">{progress}%</div>
                      <div className="absolute bottom-1 right-2 text-[10px] font-medium text-blue-800 drop-shadow">{timeProg}%</div>
                      {phase.total_value > 0 && (
                        <div className="absolute top-1 left-2 text-[9px] font-medium text-slate-600 bg-white/80 rounded px-1">
                          {phase.total_value.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Sumar Etape</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b">
                <th className="pb-2 font-medium">Etapa</th><th className="pb-2 font-medium">Perioada</th>
                <th className="pb-2 font-medium text-right">Valoare</th><th className="pb-2 font-medium text-right">Progres</th>
              </tr></thead>
              <tbody>
                {phases.map((p, i) => (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="py-2 font-medium text-slate-700">{p.name}</td>
                    <td className="py-2 text-slate-500 text-xs">{p.start_date} — {p.end_date || '-'}</td>
                    <td className="py-2 text-right text-slate-700">{p.total_value > 0 ? `${p.total_value.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei` : '-'}</td>
                    <td className="py-2 text-right">
                      <Badge variant="outline" className={`${(p.progress_percent || 0) >= 100 ? 'bg-green-50 text-green-700' : (p.progress_percent || 0) > 0 ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-500'}`}>
                        {(p.progress_percent || 0).toFixed(0)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold border-t-2">
                  <td className="pt-2">Total ({phases.length} etape)</td><td></td>
                  <td className="pt-2 text-right">{phases.reduce((s, p) => s + (p.total_value || 0), 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</td>
                  <td className="pt-2 text-right">
                    <Badge className="bg-teal-100 text-teal-700">
                      {phases.length > 0 ? (phases.reduce((s, p) => s + (p.progress_percent || 0), 0) / phases.length).toFixed(0) : 0}% mediu
                    </Badge>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
